import React from "react";
import { AvatarMedia, Loader, useWindowSize, Wrapper } from "../common";
import type { SessionUser } from "./session";

export type Scope = { name: string; description: string };

export type ClientInfo = {
	id: string;
	name: string;
	logoUrl: string | null;
	linkId: string | null;
	coverImageUrl: string | null;
	backgroundColor: string | null;
	homepageUrl: string | null;
	tosUrl: string | null;
	privacyUrl: string | null;
	scopes: Scope[];
	requireConsent: boolean;
};

export type Failure = "expired" | "invalid" | "framed" | "unavailable";

const webUrl = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	try {
		const { protocol } = new URL(value);
		return protocol === "https:" || protocol === "http:" ? value : null;
	} catch {
		return null;
	}
};

export const parseClientInfo = (data: any): ClientInfo => ({
	id: String(data.client_id),
	name: String(data.name),
	logoUrl: webUrl(data.logo_url),
	linkId: typeof data.link_id === "string" && data.link_id !== "" ? data.link_id : null,
	coverImageUrl: webUrl(data.cover_image_url),
	backgroundColor:
		typeof data.background_color === "string" && data.background_color !== ""
			? data.background_color
			: null,
	homepageUrl: webUrl(data.homepage_url),
	tosUrl: webUrl(data.tos_url),
	privacyUrl: webUrl(data.privacy_url),
	scopes: Array.isArray(data.scopes) ? data.scopes : [],
	requireConsent: data.require_consent !== false,
});

const failureCopy: Record<Failure, { title: string; body: string }> = {
	expired: {
		title: "This sign-in request has expired",
		body: "Go back to the app and try signing in again.",
	},
	invalid: {
		title: "This sign-in link isn't valid",
		body: "Go back to the app and start signing in again.",
	},
	framed: {
		title: "Open this page in its own window",
		body: "For your security, Sign in with Select can't run inside another website.",
	},
	unavailable: {
		title: "Something went wrong",
		body: "We couldn't reach Select. Check your connection and try again.",
	},
};

const buttonStyle = {
	borderRadius: "24px",
	fontSize: "16px",
	fontWeight: 700,
	lineHeight: "18px",
};

function Card({
	children,
	coverImage,
	backgroundColor,
}: {
	children: React.ReactNode;
	coverImage?: string | null;
	backgroundColor?: string | null;
}) {
	const size = useWindowSize();
	return (
		<Wrapper
			InIframe={false}
			isMobile={size.width < 481}
			coverImage={coverImage}
			backgroundColor={backgroundColor}
		>
			<div className="p-8">{children}</div>
		</Wrapper>
	);
}

function Monogram({ name, size }: { name: string; size: string }) {
	return (
		<div
			aria-hidden="true"
			className={`${size} flex flex-none items-center justify-center rounded-full bg-primary-3 font-bold text-neutral-1`}
		>
			{name.trim().charAt(0).toUpperCase() || "?"}
		</div>
	);
}

function ClientLogo({ client }: { client: ClientInfo }) {
	if (!client.logoUrl) return <Monogram name={client.name} size="h-14 w-14 text-2xl" />;
	return (
		<AvatarMedia
			src={client.logoUrl}
			alt=""
			className="h-14 w-14 flex-none rounded-full border border-neutral-11 object-cover"
		/>
	);
}

function ClientLink({ href, children }: { href: string; children: React.ReactNode }) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="text-primary-2 underline hover:text-primary-3"
		>
			{children}
		</a>
	);
}

function Policies({ client }: { client: ClientInfo }) {
	const { privacyUrl, tosUrl } = client;
	if (!privacyUrl && !tosUrl) return null;
	return (
		<p className="mb-6 text-xs text-neutral-6">
			Before allowing, see {client.name}'s{" "}
			{privacyUrl && <ClientLink href={privacyUrl}>privacy policy</ClientLink>}
			{privacyUrl && tosUrl && " and "}
			{tosUrl && <ClientLink href={tosUrl}>terms of service</ClientLink>}.
		</p>
	);
}

export type ConsentScreenProps = {
	client: ClientInfo;
	user: SessionUser;
	busy: boolean;
	onAllow: () => void;
	onDeny: () => void;
	onSwitchAccount: () => void;
};

export default function ConsentScreen({
	client,
	user,
	busy,
	onAllow,
	onDeny,
	onSwitchAccount,
}: ConsentScreenProps) {
	const appName = client.homepageUrl ? (
		<ClientLink href={client.homepageUrl}>{client.name}</ClientLink>
	) : (
		client.name
	);

	return (
		<Card coverImage={client.coverImageUrl} backgroundColor={client.backgroundColor}>
			<div aria-busy={busy}>
				<div className="mb-6 flex items-center justify-center gap-3">
					<ClientLogo client={client} />
					<span aria-hidden="true" className="tracking-widest text-neutral-8">
						•••
					</span>
					<img src="/logo.svg" alt="" className="h-14 w-14 flex-none" />
				</div>

				<h1
					className="mb-2 text-center font-bold"
					style={{ fontSize: "22px", lineHeight: "28px" }}
				>
					{appName} wants to access your Select account
				</h1>

				<div
					className="mb-6 mt-6 flex items-center gap-3 border border-neutral-11 p-3"
					style={{ borderRadius: "12px" }}
				>
					{user.avatar ? (
						<AvatarMedia
							src={user.avatar}
							alt=""
							className="h-10 w-10 flex-none rounded-full object-cover"
						/>
					) : (
						<Monogram name={user.name || user.username} size="h-10 w-10" />
					)}
					<div className="min-w-0 flex-1">
						<p className="truncate font-bold">{user.name || user.username}</p>
						{user.username && (
							<p className="truncate text-sm text-neutral-6">@{user.username}</p>
						)}
					</div>
					<button
						type="button"
						onClick={onSwitchAccount}
						disabled={busy}
						className="flex-none text-sm font-bold text-primary-2 hover:text-primary-3"
					>
						Not you?
					</button>
				</div>

				<p className="mb-3 text-sm font-bold">This will allow {client.name} to:</p>
				<ul className="mb-6 space-y-3">
					{client.scopes.map((scope) => (
						<li key={scope.name} className="flex items-start gap-3 text-sm">
							<svg
								aria-hidden="true"
								viewBox="0 0 20 20"
								className="mt-0.5 h-4 w-4 flex-none fill-primary-2"
							>
								<path d="M7.6 14.2 3.4 10l1.4-1.4 2.8 2.8 7.6-7.6L16.6 5z" />
							</svg>
							<span>{scope.description}</span>
						</li>
					))}
				</ul>

				<Policies client={client} />

				<button
					type="button"
					onClick={onAllow}
					disabled={busy}
					className="mb-3 w-full bg-primary-1 p-3 text-neutral-1 hover:bg-primary-2"
					style={buttonStyle}
				>
					Allow
				</button>
				<button
					type="button"
					onClick={onDeny}
					disabled={busy}
					className="w-full border border-neutral-11 bg-neutral-1 p-3 text-neutral-2 hover:bg-neutral-4"
					style={buttonStyle}
				>
					Cancel
				</button>

				{busy && (
					<div className="fixed inset-0 z-50">
						<div className="absolute inset-0 bg-primary-4 opacity-50" />
						<Loader />
					</div>
				)}
			</div>
		</Card>
	);
}

export function FailureScreen({
	failure,
	onRetry,
}: {
	failure: Failure;
	onRetry: () => void;
}) {
	const copy = failureCopy[failure];
	return (
		<Card>
			<div className="flex flex-col items-center text-center" role="alert">
				<img src="/logo.svg" alt="Select logo" className="mb-5 h-16 w-16" />
				<h1 className="mb-2 font-bold" style={{ fontSize: "22px", lineHeight: "28px" }}>
					{copy.title}
				</h1>
				<p className="text-sm text-neutral-6">{copy.body}</p>
				{failure === "unavailable" && (
					<button
						type="button"
						onClick={onRetry}
						className="mt-6 w-full bg-primary-1 p-3 text-neutral-1 hover:bg-primary-2"
						style={buttonStyle}
					>
						Try again
					</button>
				)}
			</div>
		</Card>
	);
}
